import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Buffer, Button, Card, Form } from '@bynder/design-system';
import { IconCut, IconSmart, IconSound } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { ImageElement } from '@bynder-studio/render-web';
import { createInMemoryCache } from '@bynder-studio/misc';
import useAccessRights from 'packages/hooks/useAccessRights';
import ContentPickerModal from 'packages/pages/editor/ContentPickerModal';
import generateTestId from '~/helpers/testIdHelpers';
import AssetsService from '~/services/AssetsService';
import useImageGenModal from 'packages/common/image-gen';
import MaybeTooltip from 'packages/common/MaybeTooltip';
import { contentIconMap, getFormattedTime } from 'packages/pages/components/card/utils';
import { CONTENT_MODAL_STEPS } from 'packages/pages/editor/ContentPickerModal/types';
import useObjectUrl from 'packages/hooks/useObjectUrl';
import useContentPreviewUrl from 'packages/hooks/useContentPreviewUrl';
import { Asset, AssetMetadataCached, AssetStatus } from 'packages/types/assets';
import features from '~/configs/features';
import { millisecondToFrames } from '~/common/editor/editorHelper';
import { typesMap } from '~/common/editor/contentSelection/helper';
import { BrowseInput, CardContextActionWrapper, CardFrame } from './index.styled';
import { ContentType, Props } from './types';

const assetsMetadataCache = createInMemoryCache<AssetMetadataCached>(30);

const toCachedValue = (asset: Asset): AssetMetadataCached => {
    const temp: Partial<AssetMetadataCached> = {
        status: asset.status,
        extension: asset.extension,
    };

    if (asset.status === AssetStatus.PROCESSED && asset.mimeType) {
        temp.mimeType = asset.mimeType;
    }

    return temp as AssetMetadataCached;
};

const BrowseButton = (props: Props) => {
    // for ContentSelectorModal
    const {
        type,
        frameRate,
        resizableRange = false,
        selectedObject,
        collectionId,
        show: showFromProps,
        handleSelectSource,
        setShowAssetSelection,
        isEditorPage,
    } = props;

    // for the rest of component
    const { fileName, disabled, mediaSrc, isAlpha, loading, testId } = props;

    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();

    const [show, setShow] = useState(showFromProps || false);
    const [modalStep, setModalStep] = useState<CONTENT_MODAL_STEPS>(CONTENT_MODAL_STEPS.CHOICE);
    const urlObject = useObjectUrl(mediaSrc);
    const previewUrl = useContentPreviewUrl(urlObject, type, isAlpha);

    const isPlayableType = type === ContentType.AUDIO || type === ContentType.VIDEO;

    const handleToggleModal = (nextShow: boolean) => {
        if (setShowAssetSelection) {
            setShowAssetSelection(nextShow);
        }

        setShow(nextShow);
    };

    const confirmSelection = useCallback(
        (item) => {
            setShow(false);
            handleSelectSource(item);

            if (setShowAssetSelection) {
                // todo: check if the passed show value is correct
                setShowAssetSelection(show);
            }
        },
        [setShow, show, handleSelectSource, setShowAssetSelection],
    );

    // polling asset status on loaded asset
    const [assetData, setAssetData] = useState<AssetMetadataCached>(null);
    const timeoutIdRef = useRef(null);

    const pollAssetStatus = async (timeout = 0) => {
        // Filter assets that were deleted and still not replaced
        if (!selectedObject.element.srcId || !selectedObject.element.src) {
            return;
        }

        const assetId = selectedObject.element.srcId;
        const cached = assetsMetadataCache.get(assetId);

        if (cached) {
            setAssetData(cached);
            return;
        }

        if (type === ContentType.IMAGE) {
            new Promise((resolve) => {
                timeoutIdRef.current = setTimeout(resolve, timeout);
            }).then(async () => {
                const { asset } = await AssetsService.getAsset(selectedObject.element.srcId, '320x320,1920x1920');

                assetsMetadataCache.set(assetId, toCachedValue(asset));
                setAssetData(toCachedValue(asset));

                if (
                    ![AssetStatus.PROCESSED, AssetStatus.FAILED, AssetStatus.VALIDATION_FAILED].includes(asset.status)
                ) {
                    pollAssetStatus(8000);
                }
            });
        }
    };

    useEffect(() => {
        pollAssetStatus();

        return () => {
            clearTimeout(timeoutIdRef.current);
        };
    }, [selectedObject.element.id]);

    // polling asset status on applying AI edit
    const addAssetToArtboard = (asset: any) => {
        const assetDuration = millisecondToFrames(asset.duration, frameRate);

        if (![AssetStatus.PROCESSED, AssetStatus.FAILED, AssetStatus.VALIDATION_FAILED].includes(asset.status)) {
            new Promise((resolve) => setTimeout(resolve, 3000))
                .then(() => AssetsService.getAsset(asset.id, '320x320,1920x1920'))
                .then((res) => {
                    if (res.status === 200) {
                        addAssetToArtboard(res.asset);
                    } else {
                        addAssetToArtboard(asset);
                    }
                });
        }

        if (asset.status === AssetStatus.PROCESSED) {
            confirmSelection({
                ...typesMap.get(type).makeObj(asset),
                offsetTime: 0,
                duration: assetDuration > selectedObject.duration ? selectedObject.duration : assetDuration,
            });
        }
    };

    const { setImageGenModal, renderImageGenModal, isAllowedToUse, isAllowedExtension } = useImageGenModal({
        handleAsset: addAssetToArtboard,
        shouldCloseAfterSave: true,
    });

    const isProcessed = assetData?.status === AssetStatus.PROCESSED;
    // Old assets do not have mimeType property, so we are using extension to determine compatibility
    const isOldAsset = isProcessed && !('mimeType' in assetData);

    const getImageAiEditContextAction = () => {
        const isFormatCompatible =
            isProcessed && !isOldAsset
                ? isAllowedToUse({ mime: assetData?.mimeType })
                : isAllowedExtension(assetData?.extension);

        if (isPlatformAllowed([features.EDIT_WITH_AI]) && isFormatCompatible && type === ContentType.IMAGE) {
            return (
                <Card.ContextActionButton
                    tooltip={
                        isProcessed
                            ? translate('editor.sidebar.shots.asset.card.image.ai-edit.label')
                            : translate('editor.sidebar.shots.asset.card.image.ai-edit.processing.label')
                    }
                    aria-label={translate('editor.sidebar.shots.asset.card.image.ai-edit.label')}
                    onClick={() => {
                        if (
                            selectedObject &&
                            selectedObject.element &&
                            selectedObject.element.srcId &&
                            selectedObject.element instanceof ImageElement
                        ) {
                            const width =
                                selectedObject.element.naturalDimension?.width ??
                                selectedObject.element.dimension.width;
                            const height =
                                selectedObject.element.naturalDimension?.height ??
                                selectedObject.element.dimension.height;
                            const fileName = selectedObject.element.fileName?.replace(/\.[^/.]+$/, '') || 'image';

                            AssetsService.prepareAIEdit(selectedObject.element.srcId).then(({ aiEditData, status }) => {
                                setImageGenModal({
                                    originalAsset: {
                                        mediaId: aiEditData.assetId,
                                        fileId: aiEditData.fileId,
                                        name: fileName,
                                        width: Math.floor(width),
                                        height: Math.floor(height),
                                    },
                                });
                            });
                        }
                    }}
                    isDisabled={disabled || !isProcessed}
                >
                    <IconSmart />
                </Card.ContextActionButton>
            );
        }

        return null;
    };

    const onImageAiIconClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    const cardContextAction = isPlayableType ? (
        <Card.ContextActionButton
            tooltip={translate(`editor.sidebar.shots.asset.card.${type}.action.label`)}
            aria-label={translate(`editor.sidebar.shots.asset.card.${type}.action.label`)}
            onClick={() => {
                setModalStep(CONTENT_MODAL_STEPS.TRIM);
                handleToggleModal(true);
            }}
            isDisabled={disabled}
        >
            <IconCut />
        </Card.ContextActionButton>
    ) : null;

    const buttonText = {
        [ContentType.IMAGE]: translate('editor.element.content.replace-image'),
        [ContentType.VIDEO]: translate('editor.element.content.replace-video'),
        [ContentType.AUDIO]: translate('editor.element.content.replace-audio'),
    };

    const placeholderText = {
        [ContentType.IMAGE]: translate('editor.element.content.placeholder.image'),
        [ContentType.VIDEO]: translate('editor.element.content.placeholder.video'),
        [ContentType.AUDIO]: translate('editor.element.content.placeholder.audio'),
    };

    const buttonTestId = generateTestId(testId);
    const inputTestId = generateTestId(`${testId}_input`);
    const selectionTestId = generateTestId(`${testId}_select`);

    return (
        <>
            {show && (
                <ContentPickerModal
                    type={type}
                    show={show}
                    modalStep={modalStep}
                    frameRate={frameRate}
                    resizableRange={resizableRange}
                    selectedObject={selectedObject}
                    collectionId={collectionId}
                    isEditorPage={isEditorPage}
                    toggleModal={handleToggleModal}
                    confirmSelection={confirmSelection}
                />
            )}
            {mediaSrc ? (
                <Card
                    variant="clean"
                    isDisabled={disabled}
                    aria-label={translate(`editor.sidebar.shots.asset.card.label`)}
                    contextAction={cardContextAction}
                    onClick={() => {
                        if (disabled) {
                            return;
                        }

                        setModalStep(CONTENT_MODAL_STEPS.CHOICE);
                        handleToggleModal(true);
                    }}
                    data-src={mediaSrc}
                    {...selectionTestId}
                >
                    <MaybeTooltip
                        hasTooltip={!!selectedObject?.element?.fileName}
                        content={selectedObject?.element?.fileName}
                        position="top-right"
                    >
                        <CardFrame
                            badge={
                                type !== ContentType.IMAGE
                                    ? getFormattedTime({ duration: selectedObject.duration, frameRate })
                                    : undefined
                            }
                            icon={contentIconMap[type]}
                            progressBar={loading && <Buffer noCornerRadius />}
                            isDisabled={disabled}
                        >
                            {type === ContentType.AUDIO ? (
                                <Card.IconPlaceholder>
                                    <IconSound />
                                </Card.IconPlaceholder>
                            ) : (
                                <Card.Images>
                                    <Card.Img src={previewUrl ?? ''} contentResize="fit" />
                                </Card.Images>
                            )}
                            <CardContextActionWrapper onClick={onImageAiIconClick}>
                                {getImageAiEditContextAction()}
                            </CardContextActionWrapper>
                        </CardFrame>
                    </MaybeTooltip>
                </Card>
            ) : (
                <Form.Group>
                    <Form.Label>{translate('editor.element.content.label')}</Form.Label>
                    <Form.InputRow>
                        <BrowseInput
                            type="text"
                            value={fileName || ''}
                            placeholder={placeholderText[type]}
                            isReadOnly
                            {...inputTestId}
                        />
                        <Button
                            onClick={() => {
                                setShow(true);
                            }}
                            isDisabled={disabled}
                            {...buttonTestId}
                        >
                            {buttonText[type]}
                        </Button>
                    </Form.InputRow>
                </Form.Group>
            )}
            {renderImageGenModal()}
        </>
    );
};

export default BrowseButton;
